import { ArrowDown, Plus } from 'lucide-react';
import { T } from '../../i18n/copy';
import Reveal from '../Reveal';
import SectionHead from '../SectionHead';
import ImageSlot from '../ImageSlot';

const SLOT_KEYS = ['grow', 'optimize', 'simplify'];

export default function Outcomes({ lang }) {
  const t = T.outcomes;
  return (
    <section data-header-theme="bone" data-testid="outcomes-section" className="bg-bone pb-8 pt-2 md:pb-10">
      <div className="editorial-margins">
        <SectionHead eyebrow={t.eyebrow[lang]} index={t.index} />
        <div className="mt-6 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-tight font-semibold text-[clamp(1.75rem,2.6vw,2.4rem)] leading-[1.08] tracking-[-0.02em] text-midnight">
              {t.headline[lang]}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:justify-self-end">
            <p className="max-w-sm text-base leading-relaxed text-midnight/70">{t.intro[lang]}</p>
          </Reveal>
        </div>
      </div>

      <div className="editorial-margins mt-5 grid grid-cols-1 gap-4 md:hidden" data-testid="outcomes-rows-mobile">
        {t.items[lang].map((item, i) => (
          <Reveal key={item.title} delay={i * 0.05} testid={`outcome-row-${i}`}>
            <div className="flex gap-4">
              <ImageSlot slotKey={SLOT_KEYS[i]} compact className="h-[72px] w-[96px] shrink-0" />
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="eyebrow text-vermilion">{`0${i + 1}`}</span>
                  <h3 className="font-tight text-lg font-semibold tracking-[0.03em] text-midnight" data-testid={`outcome-title-mobile-${i}`}>{item.title}</h3>
                </div>
                <p className="mt-1 text-base leading-relaxed text-midnight/70">{item.desc}</p>
              </div>
            </div>
            <details data-testid={`outcome-details-${i}`} className="group">
              <summary className="flex cursor-pointer list-none items-center gap-2 border-t border-midnight/12 pt-2">
                <span className="eyebrow text-steel-dark">{t.listLabel[lang]}</span>
                <Plus className="h-3.5 w-3.5 shrink-0 text-midnight/40 transition-transform duration-200 group-open:rotate-45" strokeWidth={1.5} />
              </summary>
              <ul className="flex flex-wrap gap-x-3 gap-y-1 pb-3 pt-2">
                {item.list.map((li) => (
                  <li key={li} className="text-[13px] text-steel-dark">{li}</li>
                ))}
              </ul>
            </details>
          </Reveal>
        ))}
      </div>

      <div className="editorial-margins mt-8 hidden grid-cols-1 gap-y-10 md:grid md:grid-cols-3 md:gap-x-10" data-testid="outcomes-triptych">
        {t.items[lang].map((item, i) => (
          <Reveal key={item.title} delay={i * 0.08} testid={`outcome-panel-${i}`} className="flex flex-col">
            <ImageSlot slotKey={SLOT_KEYS[i]} className="w-full" />
            <div className="mt-4 border-t border-midnight/12 pt-3">
              <div className="flex items-baseline gap-3">
                <span className="eyebrow text-vermilion">{`0${i + 1}`}</span>
                <h3 className="font-tight text-xl font-semibold tracking-[0.03em] text-midnight" data-testid={`outcome-title-${i}`}>{item.title}</h3>
              </div>
              <p className="mt-2 max-w-none text-base leading-snug text-midnight/70">{item.desc}</p>
              <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-midnight/12 pt-2.5">
                {item.list.map((li) => (
                  <li key={li} className="text-[13.5px] text-steel-dark">{li}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="editorial-margins mt-5 flex items-start gap-3">
        <ArrowDown className="mt-1.5 h-4 w-4 shrink-0 text-vermilion" strokeWidth={1.5} />
        <p className="font-serif text-xl leading-snug text-midnight md:text-2xl">
          {t.closing[lang][0]}
          <br />
          {t.closing[lang][1]}
        </p>
      </div>
    </section>
  );
}
